import React from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { selectCmsFromBootstrap, selectFeatureBehaviorFromBootstrap, selectGeoFromBootstrap } from 'store/slice/bootstrap';
import { selectTextForTextSearch, setTextSearchInSearchBoxSettingsState } from 'store/slice/home';
import { getSearchPageFromCityIdAndKeyword, getSearchPageUrl, getSearchPageUrlFromGlobalKeyword } from 'core/utils/activityUrlGenerator';
import { OverlayType, showOverlay } from 'store/slice/overlay';
import { CmsValue, Geo } from 'core/type/BootstrapConfig.types';
import { killEvent } from 'core/utils/domUtils';
import { Category, SuggestionResult } from 'core/type/SuggestionResult.types';
import { routeTo, selectSearchPageType } from 'store/slice/navigation';
import { Optional } from 'core/type/generic';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesHeaderSearchBarTextInputClickEventActivitiesFieldsV2,
    ActivitiesHeaderSearchBarTextInputSeenEventActivitiesFieldsV2,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesHeaderSearchBarTextInputClickEventActivitiesManifestV2,
    ActivitiesHeaderSearchBarTextInputSeenEventActivitiesManifestV2,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { ActivityCardProps } from 'core/type/ActivityCard.types';
import { ScreenSize, selectScreenSize } from 'store/slice/screen';
import { SearchPageType } from 'store/slice/slice.types';

interface OwnProps {
    trailingAsset?: React.JSX.Element;
    popularActivities?: ActivityCardProps[];
    isDisabledGlobalKeywordSearch?: boolean;
}

interface StateProps {
    cms: CmsValue;
    searchText: string;
    suggestionResult?: SuggestionResult;
    screenSize: ScreenSize;
    isNearByEnabled: boolean;
    shouldShowPopularCategories: boolean;
    placeholderText: string;
    isCityNamePlaceHolderEnabled: boolean;
}

interface DispatchProps {
    onSearchTextInputClick: (event?: React.MouseEvent | React.KeyboardEvent) => void;
    onSearchTextInputSeen: () => void;
    onSearchResultSelect: (result: Optional<SuggestionResult>, category?: Category) => void;
    onKeywordResultSelect: (keyword: string, geo: Geo) => void;
    onGlobalKeywordResultSelect: (keyword: string) => void;
}

export type SearchBarProps = OwnProps & StateProps & DispatchProps;

export const mapStateToProps = createSelector(
    selectCmsFromBootstrap,
    selectGeoFromBootstrap,
    selectTextForTextSearch,
    selectScreenSize,
    selectSearchPageType,
    selectFeatureBehaviorFromBootstrap,
    (cms, geo, searchText, screenSize, searchPageType, featureBehaviours): StateProps => {
        return {
            cms,
            searchText: featureBehaviours.ActivitiesCityNamePlaceholderBehavior ? '' : searchText,
            screenSize,
            suggestionResult: geo,
            isNearByEnabled: featureBehaviours.SearchBoxNearMeFeature,
            shouldShowPopularCategories: featureBehaviours.SubcategoryFilter && screenSize.mobile && searchPageType === SearchPageType.City,
            placeholderText: featureBehaviours.ActivitiesCityNamePlaceholderBehavior ? (geo ? geo.nameTranslated || geo.cityNameTranslated : '') : '',
            isCityNamePlaceHolderEnabled: featureBehaviours.ActivitiesCityNamePlaceholderBehavior,
        };
    },
);

export const mapDispatchToProps = (dispatch: Function): DispatchProps => ({
    onSearchTextInputClick: (event?: React.MouseEvent | React.KeyboardEvent) => {
        trackElementEvent<ActivitiesHeaderSearchBarTextInputClickEventActivitiesFieldsV2>(ActivitiesHeaderSearchBarTextInputClickEventActivitiesManifestV2.ids, {});
        dispatch(showOverlay(OverlayType.TextSearch));
        killEvent(event);
    },
    onSearchTextInputSeen: () => {
        trackElementEvent<ActivitiesHeaderSearchBarTextInputSeenEventActivitiesFieldsV2>(ActivitiesHeaderSearchBarTextInputSeenEventActivitiesManifestV2.ids, {});
    },
    onSearchResultSelect: (suggestion: Optional<SuggestionResult>, category?: Category) => {
        if (!suggestion) return;
        const searchPageUrl = getSearchPageUrl(suggestion, undefined, category);
        dispatch(setTextSearchInSearchBoxSettingsState(suggestion));
        dispatch(routeTo(searchPageUrl));
    },
    onKeywordResultSelect: (keyword: string, geo: Geo) => {
        const searchPageUrl = getSearchPageFromCityIdAndKeyword(geo.cityId, keyword);
        dispatch(routeTo(searchPageUrl));
    },
    onGlobalKeywordResultSelect: (keyword: string) => {
        const searchPageUrl = getSearchPageUrlFromGlobalKeyword(keyword);
        dispatch(routeTo(searchPageUrl));
    },
});
